import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import type { User, RegisterRequest, LoginRequest } from '@/api/custom/auth/model'
import { auth } from '@/api/custom/auth'
import { httpClient } from '@/api/core/http-client'
import { UserRole } from '@/types/auth'

export const useAuthStore = defineStore('auth', () => {
  const user = ref<User | null>(null)
  const token = ref<string | null>(localStorage.getItem('access_token'))
  const isLoading = ref(false)

  const isAuthenticated = computed(() => !!token.value && !!user.value)

  const setAuthToken = (newToken: string | null) => {
    token.value = newToken
    if (newToken) {
      localStorage.setItem('access_token', newToken)
      httpClient.defaults.headers.common['Authorization'] = `Bearer ${newToken}`
    } else {
      localStorage.removeItem('access_token')
      delete httpClient.defaults.headers.common['Authorization']
    }
  }

  // Инициализация токена при загрузке
  if (token.value) {
    httpClient.defaults.headers.common['Authorization'] = `Bearer ${token.value}`
  }

  const register = async (credentials: RegisterRequest) => {
    isLoading.value = true
    try {
      await auth.register({ payload: credentials })
    } finally {
      isLoading.value = false
    }
  }

  const login = async (credentials: LoginRequest) => {
    isLoading.value = true
    try {
      const response = await auth.login({ payload: credentials })
      setAuthToken(response.access_token)
      await fetchProfile()
    } finally {
      isLoading.value = false
    }
  }

  const logout = () => {
    user.value = null
    setAuthToken(null)
    window.location.href = '/login'
  }

  const fetchProfile = async () => {
    if (!token.value) return null

    isLoading.value = true
    try {
      const response = await auth.profile()
      user.value = response
    } catch (error) {
      // Если токен недействителен, очищаем состояние
      logout()
      throw error
    } finally {
      isLoading.value = false
    }
  }

  // Проверка роли пользователя
  const hasRole = (requiredRoles: UserRole[]) => {
    if (!user.value || !user.value.role) return false
    return requiredRoles.includes(user.value.role as UserRole)
  }

  const isCurator = computed(() => hasRole([UserRole.CURATOR]))

  return {
    user,
    token,
    isLoading,
    isAuthenticated,
    register,
    login,
    logout,
    fetchProfile,
    hasRole,
    isCurator,
  }
})
